/**
 * Site chrome from base.html: user menu, notifications bell poller, toast
 * host, tabs.
 *
 * Markup contract: `notifications_topbar` root carries data-endpoint;
 * `tabs` root carries data-sync-url, data-initial-tab, data-tabs and
 * optionally hx-get.
 */

import { AUTH_REFRESHED_EVENT, ensureFresh } from "../session-refresh.js";
import { listen, set_poller_param, unlisten_all } from "./helpers.js";

document.addEventListener("alpine:init", () => {
  // ARIA-compliant menu (WAI-ARIA Authoring Practices: Menu Button).
  // Roving tabindex over items with role="menuitem" / "menuitemcheckbox"
  // inside the registered scope. Keyboard: Arrow Up/Down cycle, Home/End,
  // single-character type-ahead, Escape closes and restores focus to the
  // trigger that opened the menu.
  Alpine.data("menu", () => ({
    open: false,
    activeIndex: -1,
    _trigger: null,
    _typeBuffer: "",
    _typeTimer: null,
    _items() {
      return Array.from(this.$root.querySelectorAll('[role="menuitem"],[role="menuitemcheckbox"]'));
    },
    toggle(event) {
      if (this.open) {
        this.closeMenu();
      } else {
        this.openMenu(event?.currentTarget);
      }
    },
    openMenu(trigger) {
      this._trigger = trigger || document.activeElement;
      this.open = true;
      this.$nextTick(() => this.focusItem(0));
    },
    closeMenu() {
      const trigger = this._trigger;
      this.open = false;
      this.activeIndex = -1;
      if (trigger && typeof trigger.focus === "function") {
        trigger.focus();
      }
      // Clear the cached trigger so the window-level Escape handler
      // doesn't yank focus back to the avatar on subsequent presses
      // anywhere on the page after the menu has already been closed.
      this._trigger = null;
    },
    // Alias so `@click.outside="close"` wiring continues to work; an outside
    // click should close the menu without yanking focus back to the trigger.
    close() {
      this.open = false;
      this.activeIndex = -1;
      this._trigger = null;
    },
    focusItem(i) {
      const items = this._items();
      if (!items.length) return;
      const n = items.length;
      this.activeIndex = ((i % n) + n) % n;
      items[this.activeIndex].focus();
    },
    next() {
      this.focusItem(this.activeIndex + 1);
    },
    prev() {
      this.focusItem(this.activeIndex - 1);
    },
    first() {
      this.focusItem(0);
    },
    last() {
      this.focusItem(this._items().length - 1);
    },
    typeAhead(key) {
      if (typeof key !== "string" || key.length !== 1) return;
      if (!/\S/.test(key)) return;
      this._typeBuffer += key.toLowerCase();
      clearTimeout(this._typeTimer);
      this._typeTimer = setTimeout(() => {
        this._typeBuffer = "";
      }, 500);
      const items = this._items();
      if (!items.length) return;
      const start = this.activeIndex < 0 ? 0 : (this.activeIndex + 1) % items.length;
      for (let k = 0; k < items.length; k++) {
        const i = (start + k) % items.length;
        const text = (items[i].textContent || "").trim().toLowerCase();
        if (text.startsWith(this._typeBuffer)) {
          this.focusItem(i);
          return;
        }
      }
    },
  }));

  // Visibility-aware poller for the notifications topbar count. Replaces the
  // HTMX `hx-trigger="every 45s [...]"` filter expression, which requires
  // `htmx.config.allowEval = true` and produces an `htmx:evalDisallowedError`
  // under the CSP-safe build. Dispatches an immediate fetch on init plus a
  // background tick every 45s while the tab is visible.
  Alpine.data("notifications_topbar", () => ({
    _listeners: [],
    _endpoint: "",
    _timer: null,
    init() {
      this._endpoint = this.$el.dataset.endpoint || "";
      if (!this._endpoint) return;
      this._fetch();
      this._arm();
      // Refresh once when the tab becomes visible again so a long-idle tab
      // does not show a stale count after the user returns — but only while
      // the circuit is closed, so a refocus during expiry can't re-fire a
      // doomed request the interval circuit-breaker just suppressed.
      listen(this, document, "visibilitychange", () => {
        if (this._timer !== null && document.visibilityState === "visible") this._fetch();
      });
      // Circuit-breaker: a 401 means the access token expired while this tab
      // stayed open. Continuing to poll a doomed request re-logs a browser
      // network 401 every tick — a line no JS handler can suppress — so open
      // the circuit (stop the interval) and drive a single shared silent
      // refresh. Resume on success; otherwise follow the server-directed login
      // URL. Only 401 opens the circuit; transient 5xx/network keep polling.
      listen(this, this.$el, "htmx:responseError", (evt) => {
        const xhr = evt.detail?.xhr;
        if (xhr?.status !== 401) return;
        this._stop();
        // We own recovery for our own poll: stop the shared body listener from
        // double-handling the same 401 (it would replay the badge fetch on top
        // of ours). Declarative HTMX requests elsewhere still bubble to it.
        evt.stopPropagation();
        const loginUrl = xhr.getResponseHeader("X-Auth-Login-Url") || "/auth/login";
        ensureFresh(() => {
          this._arm();
          this._fetch();
        }, loginUrl);
      });
      // Resume if some *other* flow refreshed the session first — but only
      // when our circuit is open. That flow's replay already refetched the
      // badge, so re-arm the interval without an extra immediate fetch.
      listen(this, document.body, AUTH_REFRESHED_EVENT, () => {
        if (this._timer === null) this._arm();
      });
    },
    _arm() {
      this._timer = setInterval(() => {
        if (document.visibilityState === "visible") this._fetch();
      }, 45000);
    },
    _stop() {
      if (this._timer) clearInterval(this._timer);
      this._timer = null;
    },
    _fetch() {
      try {
        htmx.ajax("GET", this._endpoint, {
          target: this.$el,
          swap: "innerHTML",
          source: this.$el,
        });
      } catch (_e) {
        // Network or HTMX errors must not crash the topbar.
      }
    },
    destroy() {
      this._stop();
      unlisten_all(this);
    },
  }));

  // Server-driven toast host (#2773). htmx dispatches a `toast` CustomEvent for
  // every `HX-Trigger: {"toast": {...}}` response header (see
  // app/routes/_fragment_redirect.py: toast_response / add_toast_header). The
  // event bubbles to <body>, where this component (mounted once in base.html,
  // outside <main> so HTMX swaps never clobber it) renders a transient,
  // auto-dismissing notification. This replaces the old URL-driven
  // ?info=/?error=/?warning= flash banners: toast text originates only
  // server-side and never travels in a URL, closing the content-spoofing
  // surface. Variant styling mirrors the `alert()` macro (no new CSS); unknown
  // variants fall back to `info`.
  Alpine.data("toast_host", () => ({
    _listeners: [],
    toasts: [],
    _seq: 0,
    init() {
      // Never read `this.$el` here or in `push`/`remove` — `this.$el` is the
      // directive element, not the toast being acted on. State lives on the
      // component scope and each toast is keyed by its own `id`.
      listen(this, document.body, "toast", (evt) => {
        const d = evt?.detail || {};
        this.push(d.message, d.variant, d.timeout_ms);
      });
    },
    push(message, variant, timeoutMs) {
      if (!message) return;
      const allowed = { info: true, success: true, error: true, warning: true };
      const v = allowed[variant] ? variant : "info";
      const id = ++this._seq;
      const role = v === "error" || v === "warning" ? "alert" : "status";
      this.toasts.push({ id: id, variant: v, message: message, role: role });
      const ms = typeof timeoutMs === "number" && timeoutMs > 0 ? timeoutMs : 6000;
      setTimeout(() => {
        this.remove(id);
      }, ms);
    },
    remove(id) {
      this.toasts = this.toasts.filter((t) => t.id !== id);
    },
    // Mirrors the per-variant Tailwind classes of the `alert()` macro
    // (_components.html) so the same strings are already in the compiled CSS.
    variantClass(variant) {
      const map = {
        success: "bg-ok-50 border-ok-500 text-ok-800",
        error: "bg-bad-50 border-bad-500 text-bad-800",
        info: "bg-blue-50 border-blue-500 text-blue-800",
        warning: "bg-warn-50 border-warn-500 text-warn-800",
      };
      return map[variant] || map.info;
    },
    destroy() {
      unlisten_all(this);
    },
  }));

  Alpine.data("tabs", () => ({
    _listeners: [],
    activeTab: "",
    _syncUrl: false,
    init() {
      this._syncUrl = this.$el.dataset.syncUrl === "true";
      const fallback = this.$el.dataset.initialTab || "";
      if (this._syncUrl) {
        const params = new URLSearchParams(window.location.search);
        const incoming = params.get("tab") || "";
        const knownTabs = this.$el.dataset.tabs ? this.$el.dataset.tabs.split(",") : [];
        this.activeTab =
          knownTabs.length === 0 || knownTabs.indexOf(incoming) >= 0
            ? incoming || fallback
            : fallback;
      } else {
        this.activeTab = fallback;
      }
      this._activateFromHash = () => {
        if (!window.location.hash || window.location.hash.length <= 1) return;
        // `decodeURIComponent` handles browser-percent-encoded fragments
        // (e.g. "Public%20User") so they still match the literal element id.
        let hashId;
        try {
          hashId = decodeURIComponent(window.location.hash.slice(1));
        } catch (_) {
          hashId = window.location.hash.slice(1);
        }
        const hashEl = document.getElementById(hashId);
        if (!hashEl || !this.$el.contains(hashEl)) return;
        const labelledBy = hashEl.getAttribute("aria-labelledby") || "";
        const hashTabId = labelledBy.replace(/^tab-/, "");
        if (!hashTabId) return;
        this.activeTab = hashTabId;
        this.$nextTick(() => {
          hashEl.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      };
      this._activateFromHash();
      listen(this, window, "hashchange", () => {
        this._activateFromHash();
      });

      // If a tab's HTMX fetch fails (network drop, 5xx), the target tabpanel
      // would otherwise sit empty with no explanation. Surface a small
      // user-visible error in the target instead.
      const onError = (evt) => {
        const t = evt.detail?.target;
        if (t.id?.indexOf("tabpanel-") !== 0) return;
        if (!this.$el.contains(t)) return;
        const status = evt.detail?.xhr ? evt.detail.xhr.status : 0;
        const msg =
          status >= 500
            ? "Couldn’t load this tab (server error). Try again in a moment."
            : status > 0
              ? `Couldn’t load this tab (error ${status}).`
              : "Couldn’t load this tab. Check your connection and try again.";
        const alert = document.createElement("div");
        alert.setAttribute("role", "alert");
        alert.className =
          "rounded-md border border-bad-200 bg-bad-50 px-4 py-3 text-sm text-bad-800";
        alert.textContent = msg;
        t.replaceChildren(alert);
      };
      listen(this, document.body, "htmx:responseError", onError);
      listen(this, document.body, "htmx:sendError", onError);
    },
    destroy() {
      unlisten_all(this);
    },
    isActive(id) {
      return this.activeTab === id;
    },
    select(id) {
      this.activeTab = id;
      if (this._syncUrl) {
        const url = new URL(window.location);
        url.searchParams.set("tab", id);
        history.replaceState(null, "", url);
      }
      // Inside this @click handler $el is the clicked button; the live
      // poller (when present) sits on the scope root, and htmx captures
      // its path at init, so a rewritten hx-get needs htmx.process to
      // pick it up.
      set_poller_param(this.$root, "active_tab", id);
    },
    _getTabButtons() {
      const tablist = this.$root.querySelector('[role="tablist"]');
      return tablist ? Array.from(tablist.querySelectorAll('[role="tab"]')) : [];
    },
    selectNext() {
      const tabs = this._getTabButtons();
      const idx = tabs.findIndex((t) => t.getAttribute("aria-selected") === "true");
      const next = tabs[(idx + 1) % tabs.length];
      if (next) {
        this.select(next.id.replace("tab-", ""));
        next.focus();
      }
    },
    selectPrev() {
      const tabs = this._getTabButtons();
      const idx = tabs.findIndex((t) => t.getAttribute("aria-selected") === "true");
      const prev = tabs[(idx - 1 + tabs.length) % tabs.length];
      if (prev) {
        this.select(prev.id.replace("tab-", ""));
        prev.focus();
      }
    },
  }));
});
